// ============================================================
// Summit League v2 — Match Dashboard Page
// Route: /matches/:matchId
//
// Layout:
//   - SeasonHeader (full width)
//   - Banner: Discord (active) / Results+Score (completed) / Contested / Info
//   - Live Stream Bar + Watch Live (full width, active matches only)
//   - Tri-column: Roster A | Match Center | Roster B
//   - Forfeit + Rules (full width)
//
// Reporting:
//   Captains report the ENTIRE SERIES at once — selecting the winner
//   of each game, then submitting all results in one action.  The
//   backend receives individual per-game reports (POST /matches/:id/
//   games/:gameNumber/report) which are sent sequentially.
//
// Access: Public read (optionalAuth).  Write endpoints require captain.
// Polls every 12s while match is active.
// ============================================================

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axiosClient from '../api/axiosClient';
import { extractError } from '../utils/formatters';
import { useAuthContext } from '../context/AuthContext';
import { useGame } from '../hooks/useGame';
import { useToast } from '../hooks/useToast';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import SeasonHeader from '../components/dashboard/SeasonHeader';
import ToastContainer from '../components/ui/Toast';
import {
  formatMatchType,
  formatMatchFormat,
  formatScheduledTime,
  DefaultTeamIcon,
} from '../components/dashboard/DashboardHelpers';

import '../styles/match-dashboard.css';

// ============================================================
// Types
// ============================================================

type MatchType = 'available_now' | 'scheduled' | 'direct_challenge';
type MatchState = 'pending' | 'accepted' | 'declined' | 'canceled' | 'expired' | 'in_progress' | 'contested' | 'completed';
type MatchFormat = 'bo1' | 'bo3' | 'bo5' | 'bo7';
type GameStatus = 'pending' | 'partial' | 'contested' | 'confirmed';

interface TeamSummary {
  teamId: number;
  teamName: string;
  teamTag: string | null;
  logoUrl: string | null;
  rank: number | null;
  points: number;
}

interface PointsChange {
  teamAPointsBefore: number;
  teamAPointsAfter: number;
  teamBPointsBefore: number;
  teamBPointsAfter: number;
}

interface MatchGameResponse {
  matchGameId: number;
  matchId: number;
  gameNumber: number;
  gameType: string;
  mapName: string;
  teamAReportedWinner: number | null;
  teamBReportedWinner: number | null;
  teamAReportedAt: string | null;
  teamBReportedAt: string | null;
  winnerTeamId: number | null;
  confirmedAt: string | null;
  status: GameStatus;
}

interface MatchWithGamesResponse {
  matchId: number;
  gameId: number;
  gameName: string;
  gameCode: string;
  seasonId: number;
  seasonName: string;
  teamA: TeamSummary;
  teamB: TeamSummary | null;
  matchType: MatchType;
  matchState: MatchState;
  matchFormat: MatchFormat;
  scheduledAt: string | null;
  expiresAt: string | null;
  acceptedAt: string | null;
  startedAt: string | null;
  completedAt: string | null;
  teamAScore: number;
  teamBScore: number;
  winnerTeamId: number | null;
  pointsChange?: PointsChange;
  creatorUserId: number;
  createdAt: string;
  updatedAt: string;
  games: MatchGameResponse[];
}

interface RosterMember {
  userId: number;
  username: string;
  avatarUrl: string | null;
  ign: string | null;
  role: 'captain' | 'member';
  joinedAt: string;
  profileUrl: string;
}

interface UserTeamInfo {
  teamId: number;
  teamName: string;
  role: 'captain' | 'member';
}

// -- Live Stream Types --------------------------------------

type StreamPlatform = 'twitch' | 'youtube' | 'twitter';

interface LiveStreamEntry {
  userId: number;
  username: string;
  avatarUrl: string | null;
  teamId: number;
  teamName: string;
  platform: StreamPlatform;
  streamUrl: string;
  declaredAt: string;
}

interface MatchLiveStreamsResponse {
  streams: LiveStreamEntry[];
  canGoLive: boolean;
  userStream: { platform: StreamPlatform; streamUrl: string } | null;
  availablePlatforms: StreamPlatform[];
}

const PLATFORM_LABELS: Record<StreamPlatform, string> = {
  twitch: 'Twitch',
  youtube: 'YouTube',
  twitter: 'X (Twitter)',
};

const PLATFORM_URLS: Record<StreamPlatform, string> = {
  twitch: 'twitch.tv',
  youtube: 'youtube.com',
  twitter: 'twitter.com',
};

// ============================================================
// Helpers
// ============================================================

function getMatchStateLabel(state: MatchState): string {
  const labels: Record<string, string> = {
    pending: 'Pending', accepted: 'Accepted', in_progress: 'In Progress',
    contested: 'Contested', completed: 'Completed', declined: 'Declined',
    canceled: 'Canceled', expired: 'Expired',
  };
  return labels[state] ?? state;
}

function getMatchStateClass(state: MatchState): string {
  const cls: Record<string, string> = {
    in_progress: 'sl-md-badge--active', accepted: 'sl-md-badge--accepted',
    completed: 'sl-md-badge--completed', contested: 'sl-md-badge--contested',
    pending: 'sl-md-badge--pending',
  };
  return cls[state] ?? 'sl-md-badge--muted';
}

function getGameStatusClass(status: GameStatus): string {
  const cls: Record<string, string> = {
    confirmed: 'sl-md-game--confirmed', partial: 'sl-md-game--partial',
    contested: 'sl-md-game--contested',
  };
  return cls[status] ?? 'sl-md-game--pending';
}

function isTerminalState(state: MatchState): boolean {
  return ['completed', 'declined', 'canceled', 'expired'].includes(state);
}

function isReportableState(state: MatchState): boolean {
  return state === 'accepted' || state === 'in_progress';
}

function hasStartTimePassed(startedAt: string | null): boolean {
  if (!startedAt) return false;
  return new Date(startedAt).getTime() <= Date.now();
}

function formatPointsDelta(before: number, after: number): string {
  const delta = after - before;
  return `${delta >= 0 ? '+' : ''}${delta.toFixed(1)} pts`;
}

function getGamesToWin(format: MatchFormat): number {
  switch (format) {
    case 'bo1': return 1;
    case 'bo3': return 2;
    case 'bo5': return 3;
    case 'bo7': return 4;
    default: return 2;
  }
}

// ============================================================
// Constants
// ============================================================

const POLL_INTERVAL_MS = 12_000;
const DISCORD_INVITE_URL = 'https://discord.gg/zh9U2Cjw9R';

// ============================================================
// Component
// ============================================================

const MatchDashboardPage: React.FC = () => {
  const { matchId: matchIdParam } = useParams<{ matchId: string }>();
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuthContext();
  const { gameId, season } = useGame();
  const toast = useToast();

  // -- Core State ------------------------------------------
  const [match, setMatch] = useState<MatchWithGamesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // -- Roster State ----------------------------------------
  const [rosterA, setRosterA] = useState<RosterMember[]>([]);
  const [rosterB, setRosterB] = useState<RosterMember[]>([]);

  // -- User Context ----------------------------------------
  const [userTeamInfo, setUserTeamInfo] = useState<UserTeamInfo | null>(null);

  // -- Series Report State ---------------------------------
  const [gameResults, setGameResults] = useState<Record<number, number>>({});
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // -- Forfeit State ---------------------------------------
  const [forfeitModalOpen, setForfeitModalOpen] = useState(false);
  const [isForfeiting, setIsForfeiting] = useState(false);

  // -- Live Stream State -----------------------------------
  const [liveStreams, setLiveStreams] = useState<LiveStreamEntry[]>([]);
  const [canGoLive, setCanGoLive] = useState(false);
  const [userStream, setUserStream] = useState<MatchLiveStreamsResponse['userStream']>(null);
  const [availablePlatforms, setAvailablePlatforms] = useState<StreamPlatform[]>([]);
  const [goLiveModalOpen, setGoLiveModalOpen] = useState(false);
  const [platformModalOpen, setPlatformModalOpen] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState<StreamPlatform | null>(null);
  const [isTogglingStream, setIsTogglingStream] = useState(false);

  // -- Edit Games State (admin only) -----------------------
  const [editGamesOpen, setEditGamesOpen] = useState(false);
  const [editSeasonData, setEditSeasonData] = useState<{
    gameTypes: string[];
    maps: string[];
    mapRestrictions: Record<string, string[]>;
  } | null>(null);
  const [editSeasonLoading, setEditSeasonLoading] = useState(false);
  const [editDraft, setEditDraft] = useState<Record<number, { gameType: string; mapName: string }>>({});
  const [isSubmittingEdits, setIsSubmittingEdits] = useState(false);

  // -- Clock -----------------------------------------------
  const [now, setNow] = useState(() => Date.now());

  const matchId = matchIdParam ? parseInt(matchIdParam, 10) : null;
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // -- Derived ---------------------------------------------

  const teamA = match?.teamA ?? null;
  const teamB = match?.teamB ?? null;
  const games = useMemo(() => match?.games ?? [], [match]);

  const teamAWins = useMemo(
    () => games.filter((g) => g.winnerTeamId === teamA?.teamId).length,
    [games, teamA]
  );
  const teamBWins = useMemo(
    () => games.filter((g) => g.winnerTeamId === teamB?.teamId).length,
    [games, teamB]
  );

  // -- Viewer Context --------------------------------------

  const viewerTeamId = useMemo(() => {
    if (!userTeamInfo || !match) return null;
    if (teamA && userTeamInfo.teamId === teamA.teamId) return teamA.teamId;
    if (teamB && userTeamInfo.teamId === teamB.teamId) return teamB.teamId;
    return null;
  }, [userTeamInfo, match, teamA, teamB]);

  const viewerIsTeamA = viewerTeamId !== null && viewerTeamId === teamA?.teamId;
  const isCaptain = userTeamInfo?.role === 'captain' && viewerTeamId !== null;
  const isAdmin = user?.role === 'admin';

  const canReport = useMemo(() => {
    if (!isCaptain || !match) return false;
    if (!isReportableState(match.matchState)) return false;
    if (!hasStartTimePassed(match.startedAt)) return false;
    return true;
  }, [isCaptain, match]);

  const hasViewerFullyReported = useMemo(() => {
    if (!viewerTeamId || games.length === 0) return false;
    return games.every((g) => {
      if (viewerIsTeamA) return g.teamAReportedWinner !== null;
      return g.teamBReportedWinner !== null;
    });
  }, [viewerTeamId, viewerIsTeamA, games]);

  const showReportForm = canReport && !hasViewerFullyReported;

  // -- Report Form Validation ------------------------------

  const gamesToWin = match ? getGamesToWin(match.matchFormat) : 2;

  const draftTeamAWins = useMemo(() => {
    if (!teamA) return 0;
    return Object.values(gameResults).filter((wId) => wId === teamA.teamId).length;
  }, [gameResults, teamA]);

  const draftTeamBWins = useMemo(() => {
    if (!teamB) return 0;
    return Object.values(gameResults).filter((wId) => wId === teamB.teamId).length;
  }, [gameResults, teamB]);

  const isDraftValid = useMemo(() => {
    return draftTeamAWins === gamesToWin || draftTeamBWins === gamesToWin;
  }, [draftTeamAWins, draftTeamBWins, gamesToWin]);

  const draftWinnerName = useMemo(() => {
    if (draftTeamAWins === gamesToWin) return teamA?.teamName ?? 'Team A';
    if (draftTeamBWins === gamesToWin) return teamB?.teamName ?? 'Team B';
    return null;
  }, [draftTeamAWins, draftTeamBWins, gamesToWin, teamA, teamB]);

  const draftScoreStr = useMemo(() => {
    if (!isDraftValid) return '';
    return draftTeamAWins === gamesToWin
      ? `${draftTeamAWins}\u2013${draftTeamBWins}`
      : `${draftTeamBWins}\u2013${draftTeamAWins}`;
  }, [isDraftValid, draftTeamAWins, draftTeamBWins, gamesToWin]);

  const isLive = match?.startedAt ? new Date(match.startedAt).getTime() <= now : false;

  const startCountdown = useMemo(() => {
    if (!match?.startedAt) return null;
    const diff = new Date(match.startedAt).getTime() - now;
    if (diff <= 0) return null;
    const totalSec = Math.ceil(diff / 1000);
    const mins = Math.floor(totalSec / 60);
    const secs = totalSec % 60;
    if (mins >= 60) {
      return `${Math.floor(mins / 60)}h ${mins % 60}m`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }, [match?.startedAt, now]);

  // -- Data Fetching ---------------------------------------

  const fetchMatch = useCallback(
    async (showLoading = false) => {
      if (!matchId || isNaN(matchId)) return;
      if (showLoading) setLoading(true);
      try {
        const res = await axiosClient.get(`/matches/${matchId}`);
        const data: MatchWithGamesResponse = res.data?.data ?? res.data;
        setMatch(data);
        setError(null);
      } catch (err: any) {
        if (showLoading) setError(extractError(err));
      } finally {
        if (showLoading) setLoading(false);
      }
    },
    [matchId]
  );

  useEffect(() => {
    if (authLoading) return;
    fetchMatch(true);
  }, [authLoading, fetchMatch]);

  // Fetch rosters
  const teamAId = match?.teamA?.teamId ?? null;
  const teamBId = match?.teamB?.teamId ?? null;

  useEffect(() => {
    if (!teamAId) return;
    let cancelled = false;
    axiosClient.get(`/teams/${teamAId}/profile`)
      .then((res) => { if (!cancelled) setRosterA(res.data?.data?.roster ?? res.data?.roster ?? []); })
      .catch(() => { if (!cancelled) setRosterA([]); });
    return () => { cancelled = true; };
  }, [teamAId]);

  useEffect(() => {
    if (!teamBId) return;
    let cancelled = false;
    axiosClient.get(`/teams/${teamBId}/profile`)
      .then((res) => { if (!cancelled) setRosterB(res.data?.data?.roster ?? res.data?.roster ?? []); })
      .catch(() => { if (!cancelled) setRosterB([]); });
    return () => { cancelled = true; };
  }, [teamBId]);

  // Fetch user team info (captain detection)
  useEffect(() => {
    if (!user || !gameId) { setUserTeamInfo(null); return; }
    let cancelled = false;

    axiosClient.get(`/profile/${user.userId}`)
      .then((res) => {
        const data = res.data?.data ?? res.data;
        const summary = data?.competitiveSummary ?? [];
        const entry = summary.find((s: any) => s.gameId === gameId && s.team);
        if (!cancelled && entry?.team) {
          setUserTeamInfo({ teamId: entry.team.teamId, teamName: entry.team.teamName, role: entry.team.role });
        } else if (!cancelled) { setUserTeamInfo(null); }
      })
      .catch(() => { if (!cancelled) setUserTeamInfo(null); });

    return () => { cancelled = true; };
  }, [user, gameId]);

  // -- Live Stream Fetching --------------------------------

  const fetchLiveStreams = useCallback(async () => {
    if (!matchId || isNaN(matchId)) return;
    try {
      const res = await axiosClient.get(`/matches/${matchId}/live-streams`);
      const data: MatchLiveStreamsResponse = res.data;
      setLiveStreams(data.streams);
      setCanGoLive(data.canGoLive);
      setUserStream(data.userStream);
      setAvailablePlatforms(data.availablePlatforms);
    } catch {
      // Silent fail — live streams are non-critical
    }
  }, [matchId]);

  useEffect(() => {
    if (!match) return;
    if (['accepted', 'in_progress'].includes(match.matchState)) {
      fetchLiveStreams();
    } else {
      setLiveStreams([]);
      setCanGoLive(false);
      setUserStream(null);
      setAvailablePlatforms([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [match?.matchState, fetchLiveStreams]);

  // -- Polling ---------------------------------------------

  const matchStateForPolling = match?.matchState ?? null;

  useEffect(() => {
    if (!matchStateForPolling || isTerminalState(matchStateForPolling)) return;
    const shouldPoll = ['accepted', 'in_progress', 'contested'].includes(matchStateForPolling);
    if (!shouldPoll) return;
    pollRef.current = setInterval(() => {
      fetchMatch(false);
      fetchLiveStreams();
    }, POLL_INTERVAL_MS);
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [matchStateForPolling, fetchMatch, fetchLiveStreams]);

  const matchStartedAt = match?.startedAt ?? null;
  useEffect(() => {
    if (!matchStartedAt || hasStartTimePassed(matchStartedAt)) return;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [matchStartedAt]);

  // -- Report Actions --------------------------------------

  const setGameWinner = (gameNumber: number, winnerTeamId: number) => {
    setGameResults((prev) => {
      const next = { ...prev };
      if (next[gameNumber] === winnerTeamId) {
        delete next[gameNumber];
      } else {
        next[gameNumber] = winnerTeamId;
      }

      let aWins = 0;
      let bWins = 0;
      const cleaned: Record<number, number> = {};
      for (const game of games) {
        const gn = game.gameNumber;
        if (next[gn] !== undefined) {
          if (next[gn] === teamA?.teamId) aWins++;
          else bWins++;
          cleaned[gn] = next[gn];
          if (aWins === gamesToWin || bWins === gamesToWin) break;
        }
      }
      return cleaned;
    });
  };

  const openConfirmModal = () => setConfirmModalOpen(true);
  const cancelReport = () => setConfirmModalOpen(false);

  const submitSeriesReport = async () => {
    if (!match || !viewerTeamId || !isDraftValid) return;
    setIsSubmitting(true);

    try {
      const sortedEntries = Object.entries(gameResults)
        .map(([gn, wId]) => ({ gameNumber: parseInt(gn, 10), winnerTeamId: wId }))
        .sort((a, b) => a.gameNumber - b.gameNumber);

      let conflictDetected = false;

      for (const entry of sortedEntries) {
        try {
          await axiosClient.post(
            `/matches/${match.matchId}/games/${entry.gameNumber}/report`,
            { teamId: viewerTeamId, winnerTeamId: entry.winnerTeamId }
          );
        } catch (innerErr: any) {
          const status = innerErr?.response?.status;
          const msg = (innerErr?.response?.data?.message || '').toLowerCase();

          // If the match became contested mid-loop, or we get a conflict/already-reported error,
          // this means the other captain submitted conflicting results. Stop reporting and alert.
          if (
            status === 409 ||
            (status === 400 && (msg.includes('cannot report') || msg.includes('already reported')))
          ) {
            conflictDetected = true;
            break;
          }

          // Unexpected error — re-throw for the outer catch
          throw innerErr;
        }
      }

      if (conflictDetected) {
        // Close the confirm modal, refresh match data, and alert the user
        setConfirmModalOpen(false);
        setGameResults({});
        await fetchMatch(false);
        toast.error(
          'Results Conflicted',
          'The opposing team submitted different results. This match is now under dispute. Please reach out to a moderator via the #moderator-request channel in Discord.'
        );
      } else {
        toast.success('Report Submitted', `Series results reported: ${draftWinnerName} wins ${draftScoreStr}`);
        setConfirmModalOpen(false);
        setGameResults({});
        await fetchMatch(false);
      }
    } catch (err: any) {
      toast.error('Report Failed', extractError(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  // -- Forfeit ---------------------------------------------

  const submitForfeit = async () => {
    if (!match || !viewerTeamId) return;
    setIsForfeiting(true);
    try {
      await axiosClient.post(`/matches/${match.matchId}/forfeit`, { teamId: viewerTeamId });
      toast.success('Match Forfeited', 'Your team has forfeited this match.');
      setForfeitModalOpen(false);
      await fetchMatch(false);
    } catch (err: any) {
      toast.error('Forfeit Failed', extractError(err));
    } finally {
      setIsForfeiting(false);
    }
  };

  // -- Edit Games Actions (admin only) ---------------------

  const openEditGames = async () => {
    if (!match || games.length === 0) return;
    const draft: Record<number, { gameType: string; mapName: string }> = {};
    for (const g of games) {
      draft[g.gameNumber] = { gameType: g.gameType, mapName: g.mapName };
    }
    setEditDraft(draft);
    setEditSeasonData(null);
    setEditGamesOpen(true);
    setEditSeasonLoading(true);
    try {
      const res = await axiosClient.get(`/admin/matches/${match.matchId}/season-config`);
      setEditSeasonData(res.data.data);
    } catch (err: any) {
      toast.error('Failed to load season config', extractError(err));
      setEditGamesOpen(false);
    } finally {
      setEditSeasonLoading(false);
    }
  };

  const submitEditGames = async () => {
    if (!match || !editSeasonData) return;
    setIsSubmittingEdits(true);
    try {
      const changedGames = games.filter((g) => {
        const draft = editDraft[g.gameNumber];
        return draft && (draft.gameType !== g.gameType || draft.mapName !== g.mapName);
      });
      if (changedGames.length === 0) {
        setEditGamesOpen(false);
        return;
      }
      for (const g of changedGames) {
        const draft = editDraft[g.gameNumber];
        await axiosClient.patch(`/admin/matches/${match.matchId}/games/${g.gameNumber}`, {
          gameType: draft.gameType,
          mapName: draft.mapName,
        });
      }
      toast.success('Games Updated', `${changedGames.length} game(s) updated successfully`);
      setEditGamesOpen(false);
      await fetchMatch(false);
    } catch (err: any) {
      toast.error('Update Failed', extractError(err));
    } finally {
      setIsSubmittingEdits(false);
    }
  };

  // -- Live Stream Actions ---------------------------------

  const handleGoLive = () => {
    if (availablePlatforms.length === 0) {
      toast.error('No Streaming Platforms', 'Set a Twitch, YouTube, or X handle on your profile first.');
      return;
    }
    if (availablePlatforms.length === 1) {
      submitGoLive(availablePlatforms[0]);
      return;
    }
    setPlatformModalOpen(true);
  };

  const submitGoLive = async (platform: StreamPlatform) => {
    if (!match) return;
    setIsTogglingStream(true);
    try {
      await axiosClient.post(`/matches/${match.matchId}/live-stream`, { platform });
      toast.success('You\'re Live!', `Streaming on ${PLATFORM_LABELS[platform]}`);
      setGoLiveModalOpen(false);
      setPlatformModalOpen(false);
      setSelectedPlatform(null);
      await fetchLiveStreams();
    } catch (err: any) {
      toast.error('Go Live Failed', extractError(err));
    } finally {
      setIsTogglingStream(false);
    }
  };

  const handleStopStreaming = async () => {
    if (!match) return;
    setIsTogglingStream(true);
    try {
      await axiosClient.delete(`/matches/${match.matchId}/live-stream`);
      toast.success('Stream Stopped', 'You are no longer listed as live.');
      await fetchLiveStreams();
    } catch (err: any) {
      toast.error('Error', extractError(err));
    } finally {
      setIsTogglingStream(false);
    }
  };

  const handleChangePlatform = () => {
    setPlatformModalOpen(true);
  };

  // -- Navigation ------------------------------------------

  const goToTeam = (id: number) => navigate(`/teams/${id}`);
  const goToProfile = (userId: number) => navigate(`/profile/${userId}`);
  const goToDashboard = () => navigate('/');
  const goToRules = () => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
    navigate('/rules');
  };

  // -- Season Header ---------------------------------------

  const seasonForHeader = useMemo(() => {
    if (season) return season;
    if (!match) return null;
    return {
      seasonId: match.seasonId, seasonName: match.seasonName, seasonNumber: 0,
      gameId: match.gameId, gameName: match.gameName, gameCode: match.gameCode,
      gameLogo: null, startDate: '', endDate: '', daysLeft: 0,
      status: 'in_progress' as const, minTeamSize: 4, maxTeamSize: 6,
      bannerUrl: null, allowedFormats: [],
    };
  }, [season, match]);

  // -- Render Helpers --------------------------------------

  const hasViewerReportedGame = (game: MatchGameResponse): boolean => {
    if (!viewerTeamId) return false;
    return viewerIsTeamA ? game.teamAReportedWinner !== null : game.teamBReportedWinner !== null;
  };

  // ============================================================
  // Render: Loading / Error
  // ============================================================

  if (loading) {
    return (
      <div className="sl-dashboard">
        <div className="sl-dashboard-inner">
          <div className="sl-card sl-md-loading">
            <div className="sl-md-loading-pulse" />
            <div className="sl-md-loading-pulse sl-md-loading-pulse--short" />
            <div className="sl-md-loading-pulse sl-md-loading-pulse--med" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="sl-dashboard">
        <div className="sl-dashboard-inner">
          <div className="sl-card sl-md-error-card">
            <h2 className="sl-md-error-title">Match could not be loaded</h2>
            <p className="sl-md-error-text">{error}</p>
            <Button variant="ghost" onClick={goToDashboard}>&larr; Back to Dashboard</Button>
          </div>
        </div>
      </div>
    );
  }

  if (!match || !teamA) {
    return (
      <div className="sl-dashboard">
        <div className="sl-dashboard-inner">
          <div className="sl-card sl-md-error-card">
            <h2 className="sl-md-error-title">Match not found</h2>
            <p className="sl-md-error-text">This match may have been canceled or expired.</p>
            <Button variant="ghost" onClick={goToDashboard}>&larr; Back to Dashboard</Button>
          </div>
        </div>
      </div>
    );
  }

  // ============================================================
  // Render: Team Score Block (for results banner)
  // ============================================================

  const renderTeamScoreBlock = (team: TeamSummary, side: 'a' | 'b', wins: number) => {
    const isViewerTeam = viewerTeamId === team.teamId;
    return (
      <div
        className={`sl-md-team ${isViewerTeam ? 'sl-md-team--viewer' : ''} sl-md-team--${side}`}
        onClick={() => goToTeam(team.teamId)}
        role="button" tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && goToTeam(team.teamId)}
      >
        <div className="sl-md-team-logo-wrap">
          {team.logoUrl ? (
            <img src={team.logoUrl} alt={team.teamName} className="sl-md-team-logo" />
          ) : (
            <div className="sl-md-team-logo sl-md-team-logo--placeholder"><DefaultTeamIcon /></div>
          )}
        </div>
        <div className="sl-md-team-info">
          <span className="sl-md-team-name">{team.teamName}</span>
          {team.teamTag && <span className="sl-md-team-tag">[{team.teamTag}]</span>}
          <span className="sl-md-team-meta">
            {team.rank ? `#${team.rank}` : 'Unranked'}
            <span className="sl-md-team-sep">&middot;</span>
            {team.points.toFixed(1)} pts
          </span>
        </div>
        <div className="sl-md-team-score">{wins}</div>
      </div>
    );
  };

  // ============================================================
  // Render: Full-Width Banner
  // ============================================================

  const renderFullWidthBanner = () => {
    // Completed -> results banner with score
    if (match.matchState === 'completed' && match.winnerTeamId) {
      const winnerName = match.winnerTeamId === teamA.teamId ? teamA.teamName : teamB?.teamName ?? 'Unknown';
      const loserName = match.winnerTeamId === teamA.teamId ? teamB?.teamName ?? 'Unknown' : teamA.teamName;
      const scoreStr = match.winnerTeamId === teamA.teamId
        ? `${teamAWins}\u2013${teamBWins}` : `${teamBWins}\u2013${teamAWins}`;

      return (
        <div className="sl-card sl-md-results-banner">
          <div className="sl-md-meta-row">
            <span className={`sl-md-badge ${getMatchStateClass(match.matchState)}`}>{getMatchStateLabel(match.matchState)}</span>
            <span className="sl-md-badge sl-md-badge--format">{formatMatchFormat(match.matchFormat)}</span>
            <span className="sl-md-badge sl-md-badge--type">{formatMatchType(match.matchType)}</span>
          </div>
          <div className="sl-md-vs-layout">
            {renderTeamScoreBlock(teamA, 'a', teamAWins)}
            <div className="sl-md-vs-center">
              <span className="sl-md-vs-label">VS</span>
              <span className="sl-md-vs-final">Final</span>
            </div>
            {teamB && renderTeamScoreBlock(teamB, 'b', teamBWins)}
          </div>
          <div className="sl-md-results-text-row">
            <p className="sl-md-results-title">{winnerName} defeated {loserName} {scoreStr}</p>
            {match.completedAt && <p className="sl-md-results-sub">Completed {formatScheduledTime(match.completedAt)}</p>}
          </div>
          {match.pointsChange && (
            <div className="sl-md-points-row">
              <div className={`sl-md-points-team ${match.winnerTeamId === teamA.teamId ? 'sl-md-points-team--winner' : 'sl-md-points-team--loser'}`}>
                <span className="sl-md-points-name">{teamA.teamName}</span>
                <span className="sl-md-points-delta">{formatPointsDelta(match.pointsChange.teamAPointsBefore, match.pointsChange.teamAPointsAfter)}</span>
              </div>
              <div className={`sl-md-points-team ${match.winnerTeamId !== teamA.teamId ? 'sl-md-points-team--winner' : 'sl-md-points-team--loser'}`}>
                <span className="sl-md-points-name">{teamB?.teamName ?? 'TBD'}</span>
                <span className="sl-md-points-delta">{formatPointsDelta(match.pointsChange.teamBPointsBefore, match.pointsChange.teamBPointsAfter)}</span>
              </div>
            </div>
          )}
        </div>
      );
    }

    // Contested
    if (match.matchState === 'contested') {
      const contestedCount = games.filter((g) => g.status === 'contested').length;
      return (
        <div className="sl-card sl-md-banner sl-md-banner--contested">
          <p className="sl-md-banner-title">Match Contested &mdash; Moderator Review Required</p>
          <p className="sl-md-banner-text">
            {contestedCount} game{contestedCount !== 1 ? 's have' : ' has'} conflicting reports. A moderator will review and finalize the outcome. Please reach out via the <strong>#moderator-request</strong> channel in Discord.
          </p>
        </div>
      );
    }

    // Active (including not-yet-started) -> Discord
    if (!isTerminalState(match.matchState)) {
      return (
        <div className="sl-card sl-md-banner sl-md-banner--discord">
          <p className="sl-md-banner-title">Discord Communication</p>
          <p className="sl-md-banner-text">
            Join the Summit League Discord server to coordinate with your opponent. Make sure your Discord tag is updated in your profile.
          </p>
          <div className="sl-md-banner-actions">
            <a className="sl-btn sl-btn--md" href={DISCORD_INVITE_URL} target="_blank" rel="noreferrer">Join Discord</a>
            {user && <Button variant="ghost" onClick={() => goToProfile(user.userId)}>Update Discord Tag</Button>}
          </div>
        </div>
      );
    }

    return null;
  };

  // ============================================================
  // Render: Roster Card
  // ============================================================

  const renderRosterCard = (team: TeamSummary, roster: RosterMember[], side: 'a' | 'b') => {
    const isViewerTeam = viewerTeamId === team.teamId;
    const sideLabel = side === 'a' ? 'Host Team' : 'Opponent';
    const sorted = [...roster].sort((a, b) => {
      if (a.role === 'captain' && b.role !== 'captain') return -1;
      if (b.role === 'captain' && a.role !== 'captain') return 1;
      return a.username.localeCompare(b.username);
    });

    return (
      <div className={`sl-card sl-md-roster-card sl-md-roster-card--${side}`}>
        <div className="sl-md-roster-header">
          <div className="sl-md-roster-logo-wrap" onClick={() => goToTeam(team.teamId)}>
            {team.logoUrl ? (
              <img src={team.logoUrl} alt={team.teamName} className="sl-md-roster-logo" />
            ) : (
              <div className="sl-md-roster-logo sl-md-roster-logo--placeholder"><DefaultTeamIcon /></div>
            )}
          </div>
          <span className="sl-md-roster-side-label">({sideLabel}){isViewerTeam ? ' \u00B7 Your Team' : ''}</span>
          <span className="sl-md-roster-team-name" onClick={() => goToTeam(team.teamId)} role="button" tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && goToTeam(team.teamId)}>{team.teamName}</span>
        </div>
        <div className="sl-md-roster-list">
          {sorted.length === 0 && <div className="sl-md-roster-empty">Loading roster...</div>}
          {sorted.map((member) => (
            <div key={member.userId} className="sl-md-roster-member"
              onClick={() => goToProfile(member.userId)} role="button" tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && goToProfile(member.userId)}>
              <div className="sl-md-roster-avatar">
                {member.avatarUrl ? <img src={member.avatarUrl} alt={member.username} /> : <span>{member.username[0]?.toUpperCase()}</span>}
              </div>
              <div className="sl-md-roster-member-info">
                <span className="sl-md-roster-username">
                  {member.username}
                  {member.role === 'captain' && <span className="sl-md-roster-captain-badge">(Captain)</span>}
                </span>
                <span className="sl-md-roster-ign">{member.ign ? `IGN: ${member.ign}` : 'No IGN set'}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // ============================================================
  // Render: Game Row
  // ============================================================

  const renderGameRow = (game: MatchGameResponse) => {
    const viewerReported = hasViewerReportedGame(game);
    const draftWinner = gameResults[game.gameNumber] ?? null;
    const isConfirmed = game.status === 'confirmed';
    const isContested = game.status === 'contested';

    const confirmedWinnerName = game.winnerTeamId === teamA.teamId
      ? teamA.teamName
      : game.winnerTeamId === teamB?.teamId ? teamB.teamName : null;

    const seriesClinched = draftTeamAWins === gamesToWin || draftTeamBWins === gamesToWin;
    const isBeyondClinch = showReportForm && seriesClinched && !gameResults[game.gameNumber];

    return (
      <div
        key={game.matchGameId}
        className={`sl-md-game-row ${getGameStatusClass(game.status)} ${
          isBeyondClinch ? 'sl-md-game-row--disabled' : ''
        }`}
      >
        <div className="sl-md-game-header">
          <div className="sl-md-game-number">Game {game.gameNumber}</div>
          <div className="sl-md-game-status-badge">
            {isConfirmed && <span className="sl-md-status-indicator sl-md-status-indicator--confirmed">Confirmed</span>}
            {game.status === 'partial' && <span className="sl-md-status-indicator sl-md-status-indicator--partial">Awaiting Report</span>}
            {isContested && <span className="sl-md-status-indicator sl-md-status-indicator--contested">Disputed</span>}
            {game.status === 'pending' && !showReportForm && <span className="sl-md-status-indicator sl-md-status-indicator--pending">Pending</span>}
          </div>
        </div>

        <div className="sl-md-game-details">
          <span className="sl-md-game-map">{game.mapName}</span>
          <span className="sl-md-game-type">{game.gameType}</span>
        </div>

        {isConfirmed && confirmedWinnerName && (
          <div className="sl-md-game-result sl-md-game-result--confirmed">{confirmedWinnerName} won</div>
        )}

        {isContested && (
          <div className="sl-md-game-result sl-md-game-result--contested">Conflicting reports &mdash; under review</div>
        )}

        {hasViewerFullyReported && !isConfirmed && !isContested && viewerReported && (
          <div className="sl-md-game-result sl-md-game-result--waiting">Your team&rsquo;s report submitted &mdash; awaiting opponent</div>
        )}

        {game.status === 'partial' && !viewerReported && !canReport && (
          <div className="sl-md-game-result sl-md-game-result--waiting">One team has reported &mdash; awaiting the other</div>
        )}

        {showReportForm && !isConfirmed && !isContested && !isBeyondClinch && teamB && (() => {
          const teamAIsYours = viewerTeamId === teamA.teamId;
          const teamAClass = teamAIsYours ? 'sl-md-toggle-btn--your' : 'sl-md-toggle-btn--opp';
          const teamBClass = teamAIsYours ? 'sl-md-toggle-btn--opp' : 'sl-md-toggle-btn--your';
          return (
            <div className="sl-md-game-toggle">
              <button
                type="button"
                className={`sl-md-toggle-btn ${teamAClass} ${draftWinner === teamA.teamId ? 'sl-md-toggle-btn--selected' : ''}`}
                onClick={() => setGameWinner(game.gameNumber, teamA.teamId)}
              >
                {teamA.teamTag ? `[${teamA.teamTag}]` : teamA.teamName}
              </button>
              <button
                type="button"
                className={`sl-md-toggle-btn ${teamBClass} ${draftWinner === teamB.teamId ? 'sl-md-toggle-btn--selected' : ''}`}
                onClick={() => setGameWinner(game.gameNumber, teamB.teamId)}
              >
                {teamB.teamTag ? `[${teamB.teamTag}]` : teamB.teamName}
              </button>
            </div>
          );
        })()}

        {isBeyondClinch && (
          <div className="sl-md-game-result sl-md-game-result--skipped">Series clinched &mdash; game not played</div>
        )}
      </div>
    );
  };

  // ============================================================
  // Render: Match Center (single card)
  // ============================================================

  const renderMatchCenter = () => {
    const notStartedYet = match.startedAt && !hasStartTimePassed(match.startedAt) && !isTerminalState(match.matchState);

    return (
    <div className="sl-card sl-md-center-card">
      <div className="sl-md-center-header">
        {notStartedYet ? (
          <>
            <span className="sl-md-center-label">Match Starts In</span>
            <span className="sl-md-center-time sl-md-center-time--countdown">{startCountdown}</span>
            <span className="sl-md-center-sublabel">
              {match.startedAt && formatScheduledTime(match.startedAt)}
            </span>
          </>
        ) : (
          <>
            <span className="sl-md-center-label">Match Start Time</span>
            <span className="sl-md-center-time">
              {match.startedAt ? formatScheduledTime(match.startedAt)
                : match.scheduledAt ? formatScheduledTime(match.scheduledAt) : 'TBD'}
            </span>
            {match.matchState === 'completed' && (
              <span className="sl-md-center-status-pill sl-md-center-status-pill--completed">Match Complete</span>
            )}
            {isLive && isReportableState(match.matchState) && (
              <span className="sl-md-center-status-pill sl-md-center-status-pill--live">Live</span>
            )}
          </>
        )}
      </div>

      <div className="sl-md-center-meta">{formatMatchType(match.matchType)}</div>

      <div className="sl-md-center-rotation-header">
        <span className="sl-md-center-rotation-title">
          Map &amp; Mode Rotation ({formatMatchFormat(match.matchFormat)})
        </span>
        {isAdmin && games.length > 0 && !isTerminalState(match.matchState) && (
          <button
            type="button"
            className="sl-md-admin-edit-btn"
            onClick={openEditGames}
          >
            Edit Games
          </button>
        )}
      </div>

      <div className="sl-md-games-list">
        {games.length === 0 ? (
          <div className="sl-md-games-empty">
            <p className="sl-md-games-empty-text">Games will appear once the match is accepted.</p>
          </div>
        ) : (
          games.map((game) => renderGameRow(game))
        )}
      </div>

      {showReportForm && games.length > 0 && (
        <div className="sl-md-report-footer">
          {isDraftValid ? (
            <>
              <p className="sl-md-report-summary">
                {draftWinnerName} wins the series {draftScoreStr}
              </p>
              <Button className="sl-md-report-submit" onClick={openConfirmModal}>
                Submit Report
              </Button>
            </>
          ) : (
            <p className="sl-md-report-hint">
              Select the winner of each game to report results.
              {Object.keys(gameResults).length > 0 && ` (${Object.keys(gameResults).length} of ${gamesToWin * 2 - 1} max games selected)`}
            </p>
          )}
        </div>
      )}

      {canReport && hasViewerFullyReported && (
        <div className="sl-md-report-footer sl-md-report-footer--submitted">
          <p className="sl-md-report-summary">Your team&rsquo;s report has been submitted.</p>
          <p className="sl-md-report-hint">
            If the other team submits conflicting results, this match will be marked as <strong>contested</strong> and escalated to a moderator.
          </p>
        </div>
      )}
    </div>
  );
  };

  // ============================================================
  // Render: Live Stream Bar (full-width card below banner)
  // ============================================================

  const renderLiveStreamBar = () => {
    if (!match || !['accepted', 'in_progress'].includes(match.matchState)) return null;
    if (!viewerTeamId) return null;

    // If user already has a stream, show the "you're live" bar
    if (userStream) {
      return (
        <div className="sl-card sl-md-live-bar">
          <div className="sl-md-live-controls">
            <span className="sl-md-live-indicator">
              <span className="sl-md-live-dot" />
              You're live on {PLATFORM_LABELS[userStream.platform]}
            </span>
            <div className="sl-md-live-controls-btns">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleChangePlatform}
                disabled={isTogglingStream}
              >
                Change Platform
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="sl-md-live-stop-btn"
                onClick={handleStopStreaming}
                disabled={isTogglingStream}
                isLoading={isTogglingStream}
              >
                Stop Streaming
              </Button>
            </div>
          </div>
        </div>
      );
    }

    // If user can go live, show the full Go Live bar
    if (!canGoLive) return null;

    return (
      <div className="sl-card sl-md-live-bar">
        <div className="sl-md-live-controls">
          <Button
            variant="ghost"
            size="sm"
            className="sl-md-go-live-btn"
            onClick={() => setGoLiveModalOpen(true)}
          >
            <span className="sl-md-live-dot sl-md-live-dot--inactive" />
            Streaming this match? Go Live!
          </Button>
        </div>
        <p className="sl-md-live-bar-hint">
          Available platforms are based on the handles you've set in your{' '}
          <span
            className="sl-md-live-bar-link"
            onClick={() => user && goToProfile(user.userId)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && user && goToProfile(user.userId)}
          >
            profile settings
          </span>
          {availablePlatforms.length > 0
            ? ` (${availablePlatforms.map((p) => PLATFORM_LABELS[p]).join(', ')})`
            : ''
          }.
        </p>
      </div>
    );
  };

  // ============================================================
  // Render: Watch Live Section
  // ============================================================

  const renderWatchLiveSection = () => {
    if (!match || !['accepted', 'in_progress'].includes(match.matchState)) return null;
    if (liveStreams.length === 0) return null;

    return (
      <div className="sl-card sl-md-watch-live-card">
        <div className="sl-md-watch-live-header">
          <span className="sl-md-live-dot" />
          <span className="sl-md-watch-live-title">Watch Live</span>
          <span className="sl-md-watch-live-count">{liveStreams.length} stream{liveStreams.length !== 1 ? 's' : ''}</span>
        </div>
        <div className="sl-md-watch-live-list">
          {liveStreams.map((stream) => {
            // Security: only render as clickable link if URL uses https://
            const isValidUrl = stream.streamUrl?.startsWith('https://');
            const Tag = isValidUrl ? 'a' : 'div';
            const linkProps = isValidUrl
              ? { href: stream.streamUrl, target: '_blank' as const, rel: 'noopener noreferrer' }
              : {};

            return (
              <Tag
                key={stream.userId}
                {...linkProps}
                className="sl-md-stream-entry"
              >
                <div className="sl-md-stream-avatar">
                  {stream.avatarUrl ? (
                    <img src={stream.avatarUrl} alt="" className="sl-md-stream-avatar-img" />
                  ) : (
                    <div className="sl-md-stream-avatar-placeholder">
                      {stream.username.charAt(0).toUpperCase()}
                    </div>
                  )}
                </div>
                <div className="sl-md-stream-info">
                  <span className="sl-md-stream-username">{stream.username}</span>
                  <span className="sl-md-stream-team">{stream.teamName}</span>
                </div>
                <span className={`sl-md-stream-platform sl-md-stream-platform--${stream.platform}`}>
                  {PLATFORM_LABELS[stream.platform]}
                </span>
                {isValidUrl && <span className="sl-md-stream-watch-label">Watch &rarr;</span>}
              </Tag>
            );
          })}
        </div>
      </div>
    );
  };

  // ============================================================
  // Render: Forfeit Section
  // ============================================================

  const renderForfeitSection = () => {
    if (!isCaptain) return null;
    if (!isReportableState(match.matchState) && match.matchState !== 'contested') return null;
    return (
      <div className="sl-md-forfeit-section">
        <Button variant="ghost" size="sm" className="sl-md-forfeit-btn" onClick={() => setForfeitModalOpen(true)}>
          Forfeit Match
        </Button>
      </div>
    );
  };

  // ============================================================
  // Render: Rules Card
  // ============================================================

  const renderRulesCard = () => (
    <div className="sl-card sl-md-rules-card">
      <p className="sl-md-rules-title">Gametypes, Rules &amp; Maps</p>
      <p className="sl-md-rules-text">View the official Summit League {match.gameName} rotation and match rules for this season.</p>
      <Button variant="ghost" onClick={goToRules}>View Rules &amp; Rotation</Button>
    </div>
  );

  // -- Confirm Modal ---------------------------------------

  const confirmMessage = isDraftValid
    ? `You are reporting the series result:\n\n${draftWinnerName} wins ${draftScoreStr}\n\nOnce submitted, this cannot be changed. If the opposing captain submits conflicting results, the match will be flagged for moderator review.`
    : '';

  // ============================================================
  // Page Output
  // ============================================================

  return (
    <>
      {/* -- Go Live Confirmation Modal -- */}
      <Modal
        isOpen={goLiveModalOpen}
        title="Go Live"
        message="Are you streaming this match live?"
        confirmLabel="Yes, I'm Live"
        cancelLabel="No"
        onConfirm={handleGoLive}
        onCancel={() => setGoLiveModalOpen(false)}
      />

      {/* -- Platform Selection Modal -- */}
      <Modal
        isOpen={platformModalOpen}
        title="Select Streaming Platform"
        confirmLabel={isTogglingStream ? 'Going Live...' : 'Go Live'}
        cancelLabel="Cancel"
        onConfirm={selectedPlatform ? () => submitGoLive(selectedPlatform) : undefined}
        onCancel={() => { setPlatformModalOpen(false); setSelectedPlatform(null); }}
        disableConfirm={!selectedPlatform || isTogglingStream}
        disableCancel={isTogglingStream}
      >
        <div className="sl-md-platform-options">
          {availablePlatforms.map((p) => (
            <button
              key={p}
              type="button"
              className={`sl-md-platform-option ${selectedPlatform === p ? 'sl-md-platform-option--selected' : ''}`}
              onClick={() => setSelectedPlatform(p)}
            >
              <span className="sl-md-platform-option-name">{PLATFORM_LABELS[p]}</span>
              <span className="sl-md-platform-option-url">{PLATFORM_URLS[p]}</span>
            </button>
          ))}
        </div>
      </Modal>

      <Modal
        isOpen={confirmModalOpen}
        title="Confirm Series Report"
        message={confirmMessage}
        confirmLabel={isSubmitting ? 'Submitting...' : 'Submit Report'}
        cancelLabel="Cancel"
        onConfirm={isSubmitting ? undefined : submitSeriesReport}
        onCancel={isSubmitting ? undefined : cancelReport}
        disableConfirm={isSubmitting}
        disableCancel={isSubmitting}
      />

      <Modal
        isOpen={forfeitModalOpen}
        title="Forfeit Match"
        message="Are you sure you want to forfeit this match? Your team will receive a loss and the series will be awarded to the opponent. This action cannot be undone."
        confirmLabel={isForfeiting ? 'Forfeiting...' : 'Forfeit Match'}
        cancelLabel="Cancel"
        onConfirm={isForfeiting ? undefined : submitForfeit}
        onCancel={isForfeiting ? undefined : () => setForfeitModalOpen(false)}
        disableConfirm={isForfeiting}
        disableCancel={isForfeiting}
      />

      {/* -- Edit Games Modal (admin only) -- */}
      <Modal
        isOpen={editGamesOpen}
        title="Edit Match Games"
        onClose={() => !isSubmittingEdits && setEditGamesOpen(false)}
      >
        {editSeasonLoading ? (
          <div className="sl-md-edit-loading">Loading season config...</div>
        ) : editSeasonData ? (
          <>
            <p className="sl-md-edit-hint">
              Edit the game type and map for unreported games. Games that have already been reported or confirmed cannot be changed.
            </p>
            <div className="sl-md-edit-games-list">
              {games.map((game) => {
                const isEditable =
                  game.teamAReportedWinner === null &&
                  game.teamBReportedWinner === null &&
                  game.winnerTeamId === null;
                const draft = editDraft[game.gameNumber] ?? { gameType: game.gameType, mapName: game.mapName };
                const mapOptions =
                  editSeasonData.mapRestrictions[draft.gameType]?.length > 0
                    ? editSeasonData.maps.filter((m) =>
                        editSeasonData.mapRestrictions[draft.gameType].includes(m)
                      )
                    : editSeasonData.maps;
                return (
                  <div
                    key={game.gameNumber}
                    className={`sl-md-edit-game-row ${!isEditable ? 'sl-md-edit-game-row--locked' : ''}`}
                  >
                    <span className="sl-md-edit-game-label">Game {game.gameNumber}</span>
                    {isEditable ? (
                      <div className="sl-md-edit-selects">
                        <select
                          className="sl-md-edit-select"
                          value={draft.gameType}
                          onChange={(e) => {
                            const newType = e.target.value;
                            setEditDraft((prev) => ({
                              ...prev,
                              [game.gameNumber]: { gameType: newType, mapName: prev[game.gameNumber]?.mapName ?? game.mapName },
                            }));
                          }}
                        >
                          {editSeasonData.gameTypes.map((gt) => (
                            <option key={gt} value={gt}>{gt}</option>
                          ))}
                        </select>
                        <select
                          className="sl-md-edit-select"
                          value={mapOptions.includes(draft.mapName) ? draft.mapName : mapOptions[0] ?? draft.mapName}
                          onChange={(e) =>
                            setEditDraft((prev) => ({
                              ...prev,
                              [game.gameNumber]: { ...prev[game.gameNumber], mapName: e.target.value },
                            }))
                          }
                        >
                          {mapOptions.map((m) => (
                            <option key={m} value={m}>{m}</option>
                          ))}
                        </select>
                      </div>
                    ) : (
                      <span className="sl-md-edit-game-locked">
                        {game.gameType} &mdash; {game.mapName} (locked)
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="sl-md-edit-footer">
              <Button
                variant="ghost"
                onClick={() => setEditGamesOpen(false)}
                disabled={isSubmittingEdits}
              >
                Cancel
              </Button>
              <Button onClick={submitEditGames} isLoading={isSubmittingEdits}>
                Save Changes
              </Button>
            </div>
          </>
        ) : null}
      </Modal>

      <div className="sl-dashboard sl-md-page">
        <div className="sl-dashboard-inner">
          <SeasonHeader season={seasonForHeader} loading={false} />
          {renderFullWidthBanner()}
          {renderLiveStreamBar()}
          {renderWatchLiveSection()}

          <div className="sl-md-tri-layout">
            <div className="sl-md-tri-col sl-md-tri-col--roster">
              {renderRosterCard(teamA, rosterA, 'a')}
            </div>
            <div className="sl-md-tri-col sl-md-tri-col--center">
              {renderMatchCenter()}
            </div>
            <div className="sl-md-tri-col sl-md-tri-col--roster">
              {teamB ? renderRosterCard(teamB, rosterB, 'b') : (
                <div className="sl-card sl-md-roster-card sl-md-roster-card--b">
                  <div className="sl-md-roster-header">
                    <div className="sl-md-roster-logo-wrap">
                      <div className="sl-md-roster-logo sl-md-roster-logo--placeholder"><DefaultTeamIcon /></div>
                    </div>
                    <span className="sl-md-roster-side-label">(Opponent)</span>
                    <span className="sl-md-roster-team-name">TBD</span>
                  </div>
                  <div className="sl-md-roster-empty">Awaiting opponent...</div>
                </div>
              )}
            </div>
          </div>

          {renderForfeitSection()}
          {renderRulesCard()}
        </div>
      </div>

      <ToastContainer toasts={toast.toasts} onRemove={toast.removeToast} />
    </>
  );
};

export default MatchDashboardPage;